import { useEffect, useRef } from "react";
import { useAuth } from "@/_core/hooks/useAuth";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import { trackCheckoutStart } from "@/lib/analytics";
import { apiPlanFromAutoBuy } from "@shared/apiPlanCarry";
import { workflowFromAutoBuy } from "@shared/workflowCarry";

/**
 * Closes the real gap in the "guest clicks Quick Buy" journey that
 * getLoginUrl()'s returnTo fix only solved half of: once someone signs
 * in and lands back on the page they started from, nothing was
 * actually resuming the purchase they clicked "Quick Buy" for -- they
 * had to notice they were back and click it a second time themselves.
 * Confirmed directly: a real guest tested this and landed back on the
 * tool page, not on a payment screen, exactly as reported.
 *
 * Mounted once, globally, in Layout.tsx (not per-page) since the
 * return page could be any of several places "Quick Buy" appears
 * (a tool page, the homepage, /pricing) -- this way it fires no matter
 * which one someone lands back on.
 *
 * Every "Quick Buy" call site's own not-authenticated branch encodes
 * which plan was wanted into the returnTo URL's autoBuy query param
 * before sending someone to sign in; this hook watches for that param
 * once they're back and authenticated, and if it's there, calls the
 * exact same billing.createCheckoutSession mutation the button itself
 * would have called, then redirects straight to the real Stripe URL --
 * so signing in finishes the job instead of just returning someone to
 * where they clicked, with the same click still ahead of them.
 */
export function useAutoResumeCheckout() {
  const { isAuthenticated } = useAuth();
  const createCheckoutSession = trpc.billing.createCheckoutSession.useMutation();
  const createPremiumCheckout = trpc.premiumApps.checkout.useMutation();
  const createApiCheckout = trpc.apiKeys.createCheckoutSession.useMutation();
  const createWorkflowCheckout = trpc.workflowProduct.checkout.useMutation();
  const createEventSuiteCheckout = trpc.eventSuite.checkout.useMutation();
  const utils = trpc.useUtils();
  const hasTriggeredRef = useRef(false);

  // The server sends someone back with ?signin=expired|failed when sign-in could not finish (expired or dropped cookies, a failed
  // token exchange). Say so once and leave the page, and its buy button, exactly as it was.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const flag = params.get("signin");
    if (flag !== "expired" && flag !== "failed") return;
    params.delete("signin");
    window.history.replaceState({}, "", window.location.pathname + (params.toString() ? `?${params}` : "") + window.location.hash);
    toast.error(flag === "expired" ? "Your sign-in took too long. Please press the button again to sign in." : "Sign-in did not finish. Please press the button again to try once more.");
  }, []);

  useEffect(() => {
    if (!isAuthenticated || hasTriggeredRef.current) return;

    const params = new URLSearchParams(window.location.search);
    const autoBuy = params.get("autoBuy");

    // API plan chosen while signed out (autoBuy=api:premium|ultra|mega): same strip-first rule, then Stripe Checkout for that plan.
    const apiPlan = apiPlanFromAutoBuy(autoBuy);
    if (apiPlan) {
      hasTriggeredRef.current = true;
      params.delete("autoBuy");
      window.history.replaceState({}, "", window.location.pathname + (params.toString() ? `?${params}` : ""));
      (async () => {
        const key = await utils.apiKeys.getMyKey.fetch();
        if (key?.plan === apiPlan) {
          toast.message("You are already on that API plan.");
          return;
        }
        // A first-time visitor has no key and none is created before payment: after paying they land on /developers?welcome=1,
        // where the key is created already on the paid plan and shown once.
        const result = await createApiCheckout.mutateAsync({ plan: apiPlan });
        trackCheckoutStart("api", "api");
        window.location.href = result.url;
      })().catch((error) => {
        console.error("Failed to auto-resume API checkout after login:", error);
        toast.error("Couldn't resume checkout after signing in. Please choose your plan again.");
      });
      return;
    }
    // Workflow Product chosen while signed out (autoBuy=workflow:<product>, or event_suite:<plan>): strip first, skip checkout when the
    // person already has it, otherwise straight to Stripe Checkout (subscription, 7-day trial for a first-time subscriber).
    const workflow = workflowFromAutoBuy(autoBuy);
    if (workflow) {
      hasTriggeredRef.current = true;
      params.delete("autoBuy");
      window.history.replaceState({}, "", window.location.pathname + (params.toString() ? `?${params}` : ""));
      (async () => {
        if (workflow.kind === "workflow") {
          const st = await utils.workflowProduct.status.fetch({ product: workflow.product });
          if (st.entitled) {
            toast.message("You already have that product.");
            return;
          }
          const result = await createWorkflowCheckout.mutateAsync({ product: workflow.product });
          window.location.href = result.url;
          return;
        }
        const st = await utils.eventSuite.status.fetch();
        if (st.entitled) {
          toast.message("You already have the Event Suite.");
          return;
        }
        const result = await createEventSuiteCheckout.mutateAsync({ plan: workflow.plan });
        window.location.href = result.url;
      })().catch((error) => {
        console.error("Failed to auto-resume workflow checkout after login:", error);
        toast.error("Couldn't resume checkout after signing in. Please press Start 7-day trial again.");
      });
      return;
    }
    if (autoBuy !== "day_pass" && autoBuy !== "monthly" && autoBuy !== "premium_day_pass" && autoBuy !== "premium_monthly") return;

    hasTriggeredRef.current = true;

    // Strip the param immediately, before the mutation even resolves --
    // if it failed partway and left the tab open, a refresh shouldn't
    // silently re-trigger a second checkout attempt.
    params.delete("autoBuy");
    const cleanedUrl = window.location.pathname + (params.toString() ? `?${params}` : "");
    window.history.replaceState({}, "", cleanedUrl);

    const start = autoBuy.startsWith("premium_")
      ? createPremiumCheckout.mutateAsync({ plan: autoBuy === "premium_monthly" ? "monthly" : "day_pass" })
      : createCheckoutSession.mutateAsync({ plan: autoBuy === "monthly" ? "monthly" : "day_pass" });
    start
      .then((result) => {
        trackCheckoutStart(autoBuy.endsWith("monthly") ? "monthly" : "day_pass", autoBuy.startsWith("premium_") ? "premium" : "professional");
        window.location.href = result.url;
      })
      .catch((error) => {
        console.error("Failed to auto-resume checkout after login:", error);
        toast.error("Couldn't resume checkout after signing in. Please click Quick Buy again.");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);
}
